import React from 'react'
import { Box, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core'
import {
  getFunctionDisplayName,
  editableFunctions,
  formatFilterValue,
} from 'src/components/chatbot/utils/functionMapping'
import { makeStyles } from '@material-ui/styles'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import TruncatedChip from './TruncatedChip'
import {
  GeneFilterModal,
  ChromosomeFilterModal,
  PositionFilterModal,
  AcmgFilterModal,
  FrequencyRangeFilterModal,
  GenotypeFilterModal,
  MultiSampleAnalysisModal,
  InDbsnpFilterModal,
  VariantCallingFilterModal,
} from './filterModals'
import ImpactFilters from 'src/views/VariantsView/Toolbar/FiltersPanelMenu/ImpactFilters'
import PhenotypeFilter from 'src/views/VariantsView/Toolbar/FiltersPanelMenu/PhenotypeFilter'
import HPOFilter from 'src/views/VariantsView/Toolbar/FiltersPanelMenu/HPOFilter'
import PanelsFilter from 'src/components/PanelsFilter'
import { useState } from 'react'

const useStyles = makeStyles((theme) => ({
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  chip: {
    fontSize: '0.75rem',
    height: 24,
  },
  chipWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover .action-buttons': {
      opacity: 1,
      visibility: 'visible',
    },
  },
  readOnlyChipWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    opacity: 0.7,
    '&:hover .action-buttons': {
      opacity: 0,
      visibility: 'hidden',
    },
  },
  actionButtons: {
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
    marginLeft: theme.spacing(0.5),
  },
}))

// Format the parameters of execution plan to display in the tooltip
const formatParameters = (params, functionName, idToName) => {
  if (!params || Object.keys(params).length === 0) return 'No parameters'

  // Determine which type of mapping to use based on the function name
  let mappingType = null
  if (functionName === 'sample_context') mappingType = 'sample'
  else if (functionName === 'HPO_filter') mappingType = 'hpo'
  else if (functionName === 'disease_filter') mappingType = 'disease'
  else if (functionName === 'panel_filter') mappingType = 'panel'

  // Special handling for position filter to combine start and end
  if (functionName === 'position_filter') {
    const start = params.position_start || 'any'
    const end = params.position_end || 'any'
    return `${start} - ${end}`
  }

  // Special handling for edit_hpos to use hpo_ids
  if (functionName === 'edit_hpos') {
    const hpoIds = params.hpo_ids || []
    if (hpoIds.length === 0) return 'No HPO terms selected'

    const formattedHpos = hpoIds
      .map((id) => {
        const hpoName = idToName.hpo?.[id] || id
        return hpoName
      })
      .join(', ')

    return formattedHpos
  }

  // Special handling for multi_sample_analysis_filter
  if (functionName === 'multi_sample_analysis_filter') {
    const samples = params.samples || []
    if (samples.length === 0) return 'No samples selected'

    const formattedSamples = samples
      .map((sample) => {
        const sampleName = idToName.sample?.[sample.sample_id] || sample.sample_id
        return sampleName
      })
      .join(', ')

    return formattedSamples
  }

  return Object.entries(params)
    .map(([key, value]) => {
      let formattedValue = value

      // Special handling for specific filter types
      if (functionName === 'gene_filter' && key === 'genes') {
        formattedValue = value || 'No genes selected'
      } else if (functionName === 'chromosome_filter' && key === 'chromosome') {
        formattedValue = value || 'No chromosome specified'
      } else if (functionName === 'disease_filter' && key === 'disease_list') {
        // Convert OMIM IDs to disease names for display using dedicated mapping
        if (Array.isArray(value)) {
          // diseaseIdToNameMapping is kept in store
          const { diseaseIdToNameMapping } = require('src/components/chatbot/stores/chatbotStore').default.getState()
          console.log(diseaseIdToNameMapping)
          const names = value
            .map((id) => {
              const name = (diseaseIdToNameMapping && diseaseIdToNameMapping[id]) || idToName.disease?.[id]
              if (!name) console.log('FunctionChips: missing disease name for OMIM id', id)
              return name || null
            })
            .filter(Boolean)
          formattedValue = names.join(', ')
        } else {
          formattedValue = value || 'No diseases selected'
        }
      } else if (functionName === 'HPO_filter' && key === 'HPO_list') {
        // Convert HPO IDs to HPO names for display
        if (Array.isArray(value)) {
          formattedValue = value.map((id) => idToName.hpo?.[id] || id).join(', ')
        } else {
          formattedValue = value || 'No HPO terms selected'
        }
      } else if (functionName === 'panel_filter' && key === 'panel_id') {
        // Convert panel ID to panel name for display
        if (value) {
          formattedValue = idToName.panel?.[value] || value
        } else {
          formattedValue = 'No panel selected'
        }
      } else if (functionName === 'impact_filter' && key === 'impact') {
        // Format impact values for display
        if (Array.isArray(value)) {
          formattedValue = value.map((v) => formatFilterValue(v)).join(', ')
        } else {
          formattedValue = formatFilterValue(value) || 'No impact selected'
        }
      } else if (functionName === 'ClinVar_filter' && (key === 'clinvar' || key === 'clinVar' || key === 'ClinVar')) {
        // Format clinvar values for display
        if (Array.isArray(value)) {
          formattedValue = value.map((v) => formatFilterValue(v)).join(', ')
        } else {
          formattedValue = formatFilterValue(value) || 'No ClinVar selected'
        }
      } else if (functionName === 'in_dbsnp_filter' && key === 'in_dbsnp') {
        // Format in_dbsnp values for display
        if (value === true || value === 'true') {
          formattedValue = 'True'
        } else if (value === false || value === 'false') {
          formattedValue = 'False'
        } else {
          formattedValue = 'True' // Default to True if no value
        }
      } else if (functionName === 'Variant_Calling_Filter' && key === 'GATK_HC') {
        // Format variant_calling values for display
        formattedValue = value || 'PASS' // Default to PASS if no value
      } else if (Array.isArray(value) && mappingType) {
        formattedValue = value
          .map((id) => {
            // Special handling for HPO IDs to add "HP:" prefix
            if (mappingType === 'hpo' && !id.startsWith('HP:')) {
              return idToName[mappingType]?.[`HP:${id}`] || id
            }
            return idToName[mappingType]?.[id] || id
          })
          .join(', ')
      } else if (typeof value === 'string' && mappingType) {
        if (mappingType === 'hpo' && !value.startsWith('HP:')) {
          formattedValue = idToName[mappingType]?.[`HP:${value}`] || value
        } else {
          formattedValue = idToName[mappingType]?.[value] || value
        }
      } else if (Array.isArray(value)) {
        formattedValue = value.join(', ')
      } else if (typeof value === 'object' && value !== null) {
        formattedValue = JSON.stringify(value)
      }

      return `${formattedValue}`
    })
    .join(', ')
}

const truncateText = (text, maxLength = 40) => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Component to display the execution plan as a series of chips
 */
const FunctionChips = ({ tools = [], onToolsChange, isLastMessage = true }) => {
  const classes = useStyles()
  const { idToName } = useChatbotStore()
  const [editIndex, setEditIndex] = useState(null)
  const [editValue, setEditValue] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [acmgModalValue, setAcmgModalValue] = useState([])
  const [geneModalValue, setGeneModalValue] = useState('')
  const [chromosomeModalValue, setChromosomeModalValue] = useState('')
  const [positionModalValue, setPositionModalValue] = useState([null, null])
  const [genotypeModalValue, setGenotypeModalValue] = useState([])
  const [impactModalValue, setImpactModalValue] = useState([])
  const [clinvarModalValue, setClinvarModalValue] = useState([])
  const [diseaseModalValue, setDiseaseModalValue] = useState([])
  const [panelModalValue, setPanelModalValue] = useState([])
  const [hpoModalValue, setHpoModalValue] = useState([])
  const [editHposModalValue, setEditHposModalValue] = useState([])
  const [multiSampleModalValue, setMultiSampleModalValue] = useState([])
  const [multiSampleModalOpen, setMultiSampleModalOpen] = useState(false)
  const [inDbsnpModalValue, setInDbsnpModalValue] = useState('true')
  const [variantCallingModalValue, setVariantCallingModalValue] = useState('PASS')

  if (!tools || tools.length === 0) return null

  const handleEdit = (index) => {
    setEditIndex(index)
    setEditValue(tools[index].args)
    if (tools[index].name === 'ACMG_filter') {
      setAcmgModalValue(tools[index].args?.ACMG || [])
    } else if (tools[index].name === 'gene_filter') {
      // Accept both array and string from backend
      let genes = tools[index].args?.genes || ''
      if (Array.isArray(genes)) {
        genes = genes.join(',')
      }
      setGeneModalValue(genes)
    } else if (tools[index].name === 'chromosome_filter') {
      setChromosomeModalValue(tools[index].args?.chromosome || '')
    } else if (tools[index].name === 'position_filter') {
      setPositionModalValue([tools[index].args?.position_start || null, tools[index].args?.position_end || null])
    } else if (tools[index].name === 'genotype_filter') {
      setGenotypeModalValue(tools[index].args?.genotype || [])
    } else if (tools[index].name === 'impact_filter') {
      setImpactModalValue(tools[index].args?.impact || [])
    } else if (tools[index].name === 'ClinVar_filter') {
      // Try different possible property names
      const clinvarValue = tools[index].args?.clinvar || tools[index].args?.clinVar || tools[index].args?.ClinVar || []
      setClinvarModalValue(clinvarValue)
    } else if (tools[index].name === 'disease_filter') {
      // Convert OMIM IDs to phenotype objects using dedicated mapping
      const diseaseIds = tools[index].args?.disease_list || []
      const { diseaseIdToNameMapping } = require('src/components/chatbot/stores/chatbotStore').default.getState()
      const fallback = idToName.disease || {}
      const diseaseObjects = diseaseIds
        .map((id) => {
          const diseaseName = (diseaseIdToNameMapping && diseaseIdToNameMapping[id]) || fallback[id]
          if (!diseaseName) {
            console.log('FunctionChips: cannot resolve disease name for OMIM id while opening edit', id)
            return null
          }
          return {
            key: id,
            disease: diseaseName,
            omim: id,
          }
        })
        .filter(Boolean)
      setDiseaseModalValue(diseaseObjects)
    } else if (tools[index].name === 'panel_filter') {
      // Convert panel ID to proper format using the cache
      const panelId = tools[index].args?.panel_id
      const cachedPanels = idToName.panel || {}
      const panelName = cachedPanels[panelId] || panelId
      const panelObject = panelId
        ? {
            id: panelId,
            name: panelName,
          }
        : null
      setPanelModalValue(panelObject ? [panelObject] : [])
    } else if (tools[index].name === 'HPO_filter') {
      // Convert HPO IDs to proper format using the cache
      const hpoIds = tools[index].args?.HPO_list || []
      const cachedHpo = idToName.hpo || {}
      const hpoObjects = hpoIds.map((id) => {
        const hpoName = cachedHpo[id] || id
        return {
          value: id,
          label: `${hpoName} (${id})`,
          name: hpoName,
          hpo_id: id,
        }
      })
      setHpoModalValue(hpoObjects)
    } else if (tools[index].name === 'edit_hpos') {
      const hpoIds = tools[index].args?.hpo_ids || []
      const cachedHpo = idToName.hpo || {}
      const hpoObjects = hpoIds.map((id) => {
        const hpoName = cachedHpo[id] || id
        return {
          value: id,
          label: `${hpoName} (${id})`,
          name: hpoName,
          hpo_id: id,
        }
      })
      setEditHposModalValue(hpoObjects)
    } else if (tools[index].name === 'in_dbsnp_filter') {
      const backendValue = tools[index].args?.in_dbsnp
      // Convert boolean to string for radio buttons, default to true
      const stringValue = backendValue === true ? 'true' : backendValue === false ? 'false' : 'true'
      setInDbsnpModalValue(stringValue)
    } else if (tools[index].name === 'Variant_Calling_Filter') {
      console.log('DEBUG: Variant_Calling_Filter args:', tools[index].args)
      setVariantCallingModalValue(tools[index].args?.GATK_HC || 'PASS')
    } else if (tools[index].name === 'multi_sample_analysis_filter') {
      // Map sample IDs to names using the cache
      const samples = tools[index].args?.samples || []
      const mappedSamples = samples.map((sample) => ({
        ...sample,
        sample_id: sample.sample_id,
        // Use cached name if available, otherwise use the ID
        sample_name: idToName.sample?.[sample.sample_id] || sample.sample_id,
      }))
      setMultiSampleModalValue(mappedSamples)
      setMultiSampleModalOpen(true)
      return // Don't set modalOpen for this case since we're using a different modal
    }
    setModalOpen(true)
  }

  const handleDelete = (index) => {
    const newTools = tools.filter((_, i) => i !== index)
    onToolsChange && onToolsChange(newTools)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditIndex(null)
    setEditValue(null)
    setAcmgModalValue([])
    setGeneModalValue('')
    setChromosomeModalValue('')
    setPositionModalValue([null, null])
    setGenotypeModalValue([])
    setImpactModalValue([])
    setClinvarModalValue([])
    setDiseaseModalValue([])
    setPanelModalValue([])
    setHpoModalValue([])
    setEditHposModalValue([])
    setMultiSampleModalValue([])
    setMultiSampleModalOpen(false)
    setInDbsnpModalValue('true')
    setVariantCallingModalValue('PASS')
  }

  const handleModalSave = () => {
    if (editIndex == null) return
    const newTools = [...tools]
    if (tools[editIndex].name === 'ACMG_filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, ACMG: acmgModalValue },
      }
    } else if (tools[editIndex].name === 'gene_filter') {
      // Save as array if backend expects array, else as string
      let genes = geneModalValue
      if (typeof genes === 'string') {
        genes = genes
          .split(',')
          .map((g) => g.trim())
          .filter(Boolean)
      }
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, genes },
      }
    } else if (tools[editIndex].name === 'chromosome_filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, chromosome: chromosomeModalValue },
      }
    } else if (tools[editIndex].name === 'position_filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, position_start: positionModalValue[0], position_end: positionModalValue[1] },
      }
    } else if (tools[editIndex].name === 'genotype_filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, genotype: genotypeModalValue },
      }
    } else if (tools[editIndex].name === 'impact_filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, impact: impactModalValue },
      }
    } else if (tools[editIndex].name === 'ClinVar_filter') {
      // Use the same property name that was found during edit
      const clinvarProperty =
        tools[editIndex].args?.clinvar !== undefined
          ? 'clinvar'
          : tools[editIndex].args?.clinVar !== undefined
          ? 'clinVar'
          : tools[editIndex].args?.ClinVar !== undefined
          ? 'ClinVar'
          : 'clinvar'
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, [clinvarProperty]: clinvarModalValue },
      }
    } else if (tools[editIndex].name === 'disease_filter') {
      // Convert phenotype objects back to OMIM IDs
      const diseaseIds = diseaseModalValue.map((disease) => disease.omim || disease.key)
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, disease_list: diseaseIds },
      }

      // Update disease mapping with the edited diseases to preserve them for display
      const { setDiseaseMappingFromSelections } =
        require('src/components/chatbot/stores/chatbotStore').default.getState()
      setDiseaseMappingFromSelections(diseaseModalValue)
    } else if (tools[editIndex].name === 'panel_filter') {
      // Convert panel objects back to ID
      const panelId = panelModalValue.length > 0 ? panelModalValue[0].id : null
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, panel_id: panelId },
      }
    } else if (tools[editIndex].name === 'HPO_filter') {
      // Convert HPO objects back to IDs
      const hpoIds = hpoModalValue.map((hpo) => hpo.hpo_id)
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, HPO_list: hpoIds },
      }
    } else if (tools[editIndex].name === 'edit_hpos') {
      const hpoIds = editHposModalValue.map((hpo) => hpo.hpo_id)
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, hpo_ids: hpoIds },
      }
    } else if (tools[editIndex].name === 'multi_sample_analysis_filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, samples: multiSampleModalValue },
      }
    } else if (tools[editIndex].name === 'in_dbsnp_filter') {
      // Convert string to boolean for backend
      const booleanValue = inDbsnpModalValue === 'true'
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, in_dbsnp: booleanValue },
      }
    } else if (tools[editIndex].name === 'Variant_Calling_Filter') {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: { ...editValue, GATK_HC: variantCallingModalValue },
      }
    } else {
      newTools[editIndex] = {
        ...newTools[editIndex],
        args: editValue,
      }
    }
    onToolsChange && onToolsChange(newTools)
    handleModalClose()
  }

  const renderEditModal = () => {
    if (editIndex == null) return null
    const tool = tools[editIndex]
    if (tool.name === 'ACMG_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose}>
          <DialogTitle>Edit ACMG Filter</DialogTitle>
          <DialogContent>
            <AcmgFilterModal
              value={acmgModalValue}
              onChange={(v) => {
                setAcmgModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (
      [
        'genesus_frequency_filter',
        'KG_frequency_filter',
        'GnomAD_Exome_frequency_filter',
        'GnomAD_Genome_frequency_filter',
        'ExAC_frequency_filter',
        'TurkishVariome_frequency_filter',
        'Alfa_Total_frequency_filter',
        'CADD_Phred_filter',
        'DANN_score_filter',
        'MetalR_score_filter',
        'PolyPhen_score_filter',
        'SIFT_score_filter',
        'AlphaMissense_score_filter',
        'Quality_Score_filter',
        'Strand_Bias_filter',
        'Read_Depth_filter',
        'Allelic_Balance_filter',
      ].includes(tool.name)
    ) {
      // Map tool name to display title, min, max, and value keys
      const rangeMeta = {
        genesus_frequency_filter: {
          title: 'Genesus Frequency',
          min: 0,
          max: 1,
          start: 'genesus_frequency_range_start',
          end: 'genesus_frequency_range_end',
        },
        KG_frequency_filter: {
          title: '1000 Genomes Frequency',
          min: 0,
          max: 1,
          start: 'KG_frequency_range_start',
          end: 'KG_frequency_range_end',
        },
        GnomAD_Exome_frequency_filter: {
          title: 'GnomAD Exome Frequency',
          min: 0,
          max: 1,
          start: 'GnomAD_Exome_frequency_range_start',
          end: 'GnomAD_Exome_frequency_range_end',
        },
        GnomAD_Genome_frequency_filter: {
          title: 'GnomAD Genome Frequency',
          min: 0,
          max: 1,
          start: 'GnomAD_Genome_frequency_range_start',
          end: 'GnomAD_Genome_frequency_range_end',
        },
        ExAC_frequency_filter: {
          title: 'ExAC Frequency',
          min: 0,
          max: 1,
          start: 'ExAC_frequency_range_start',
          end: 'ExAC_frequency_range_end',
        },
        TurkishVariome_frequency_filter: {
          title: 'Turkish Variome Frequency',
          min: 0,
          max: 1,
          start: 'TurkishVariome_frequency_range_start',
          end: 'TurkishVariome_frequency_range_end',
        },
        Alfa_Total_frequency_filter: {
          title: 'ALFA Frequency',
          min: 0,
          max: 1,
          start: 'Alfa_Total_frequency_range_start',
          end: 'Alfa_Total_frequency_range_end',
        },
        CADD_Phred_filter: {
          title: 'CADD Score',
          min: -10000,
          max: 10000,
          start: 'CADD_Phred_range_start',
          end: 'CADD_Phred_range_end',
        },
        DANN_score_filter: {
          title: 'DANN Score',
          min: 0,
          max: 1,
          start: 'DANN_score_range_start',
          end: 'DANN_score_range_end',
        },
        MetalR_score_filter: {
          title: 'MetaLR Score',
          min: 0,
          max: 1,
          start: 'MetalR_score_range_start',
          end: 'MetalR_score_range_end',
        },
        PolyPhen_score_filter: {
          title: 'PolyPhen Score',
          min: 0,
          max: 1,
          start: 'PolyPhen_score_range_start',
          end: 'PolyPhen_score_range_end',
        },
        SIFT_score_filter: {
          title: 'SIFT Score',
          min: 0,
          max: 1,
          start: 'SIFT_score_range_start',
          end: 'SIFT_score_range_end',
        },
        AlphaMissense_score_filter: {
          title: 'AlphaMissense Score',
          min: 0,
          max: 1,
          start: 'AlphaMissense_score_range_start',
          end: 'AlphaMissense_score_range_end',
        },
        Quality_Score_filter: {
          title: 'Quality Score',
          min: 0,
          max: 150000,
          start: 'Quality_Score_range_start',
          end: 'Quality_Score_range_end',
        },
        Strand_Bias_filter: {
          title: 'Strand Bias',
          min: 0,
          max: 1000,
          start: 'Strand_Bias_range_start',
          end: 'Strand_Bias_range_end',
        },
        Read_Depth_filter: {
          title: 'Read Depth',
          min: 0,
          max: 1000,
          start: 'Read_Depth_range_start',
          end: 'Read_Depth_range_end',
        },
        Allelic_Balance_filter: {
          title: 'Allelic Balance',
          min: 0,
          max: 1000,
          start: 'Allelic_Balance_range_start',
          end: 'Allelic_Balance_range_end',
        },
      }
      const meta = rangeMeta[tool.name]
      return (
        <Dialog open={modalOpen} onClose={handleModalClose}>
          <DialogTitle>Edit {meta.title} Filter</DialogTitle>
          <DialogContent>
            <FrequencyRangeFilterModal
              title={meta.title}
              min={meta.min}
              max={meta.max}
              value={[editValue?.[meta.start] ?? meta.min, editValue?.[meta.end] ?? meta.max]}
              onChange={(v) => {
                setEditValue({
                  ...editValue,
                  [meta.start]: v[0],
                  [meta.end]: v[1],
                })
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'gene_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Gene Filter</DialogTitle>
          <DialogContent>
            <GeneFilterModal
              value={geneModalValue}
              onChange={(v) => {
                setGeneModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'chromosome_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Chromosome Filter</DialogTitle>
          <DialogContent>
            <ChromosomeFilterModal
              value={chromosomeModalValue}
              onChange={(v) => {
                setChromosomeModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'position_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Position Filter</DialogTitle>
          <DialogContent>
            <PositionFilterModal
              value={positionModalValue}
              onChange={(v) => {
                setPositionModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'genotype_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Genotype Filter</DialogTitle>
          <DialogContent>
            <GenotypeFilterModal
              value={genotypeModalValue}
              onChange={(v) => {
                setGenotypeModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'impact_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Impact Filter</DialogTitle>
          <DialogContent>
            <ImpactFilters
              impactEvent={impactModalValue}
              setImpactEvent={(v) => {
                setImpactModalValue(v)
              }}
              filterType="impact"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'ClinVar_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Clinvar Filter</DialogTitle>
          <DialogContent>
            <ImpactFilters
              impactEvent={clinvarModalValue}
              setImpactEvent={(v) => {
                setClinvarModalValue(v)
              }}
              filterType="clinvar"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'disease_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Disease Filter</DialogTitle>
          <DialogContent>
            <PhenotypeFilter
              phenotypes={diseaseModalValue}
              setPhenotypes={(v) => {
                setDiseaseModalValue(v)
              }}
              showInheritance={false}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'panel_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Panel Filter</DialogTitle>
          <DialogContent>
            <PanelsFilter
              selectedPanels={panelModalValue}
              setSelectedPanels={(v) => {
                setPanelModalValue(v)
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'HPO_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit HPO Filter</DialogTitle>
          <DialogContent>
            <HPOFilter
              hpoTerms={hpoModalValue}
              setHpoTerms={(v) => {
                setHpoModalValue(v)
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'edit_hpos') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit HPO Filter</DialogTitle>
          <DialogContent>
            <HPOFilter
              hpoTerms={editHposModalValue}
              setHpoTerms={(v) => {
                setEditHposModalValue(v)
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'in_dbsnp_filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose}>
          <DialogTitle>Edit dbSNP Filter</DialogTitle>
          <DialogContent>
            <InDbsnpFilterModal
              value={inDbsnpModalValue}
              onChange={(v) => {
                setInDbsnpModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'Variant_Calling_Filter') {
      return (
        <Dialog open={modalOpen} onClose={handleModalClose}>
          <DialogTitle>Edit Variant Calling Filter</DialogTitle>
          <DialogContent>
            <VariantCallingFilterModal
              value={variantCallingModalValue}
              onChange={(v) => {
                setVariantCallingModalValue(v)
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleModalSave} color="primary" variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )
    } else if (tool.name === 'multi_sample_analysis_filter') {
      return (
        <Dialog open={multiSampleModalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>Edit Multi-Sample Analysis Filter</DialogTitle>
          <DialogContent>
            <MultiSampleAnalysisModal
              value={multiSampleModalValue}
              onChange={(v) => {
                setMultiSampleModalValue(v)
                // Update the tools immediately when the panel saves
                if (editIndex !== null) {
                  const newTools = [...tools]
                  newTools[editIndex] = {
                    ...newTools[editIndex],
                    args: { ...editValue, samples: v },
                  }
                  onToolsChange && onToolsChange(newTools)
                }
              }}
              onClose={handleModalClose}
            />
          </DialogContent>
        </Dialog>
      )
    }
    return null
  }

  return (
    <Box className={classes.chipContainer}>
      {/* {!isUserMessage && (
        <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 1, alignSelf: 'center' }}>
          To apply:
        </Box>
      )} */}
      {tools.map((step, index) => {
        const functionName = step.name
        const displayName = getFunctionDisplayName(functionName)
        const formattedParams = formatParameters(step.args, functionName, idToName)
        const fullDisplayText = `${displayName}: ${formattedParams}`
        const truncatedDisplayText = truncateText(fullDisplayText)
        return (
          <Tooltip key={index} title={fullDisplayText} arrow>
            <Box className={classes.chipWrapper}>
              <TruncatedChip
                size="small"
                label={truncatedDisplayText}
                className={classes.chip}
                color="primary"
                variant="outlined"
                maxWidth={200}
                showTooltip={false} // We're already using a Tooltip wrapper
                chipProps={{
                  style: { marginRight: 4 },
                }}
              />
              <Box className={`${classes.actionButtons} action-buttons`}>
                {editableFunctions.includes(functionName) && isLastMessage && (
                  <IconButton size="small" onClick={() => handleEdit(index)} aria-label="edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {editableFunctions.includes(functionName) && isLastMessage && (
                  <IconButton size="small" onClick={() => handleDelete(index)} aria-label="delete">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Tooltip>
        )
      })}
      {renderEditModal()}
    </Box>
  )
}

export default FunctionChips
